import { ChevronLeft, ChevronRight, Lock, RotateCcw } from "lucide-react"
import * as React from "react"

import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { Segmented } from "@/components/ui/segmented"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { AUDIT_PAGE_SIZE, ApiError, getAuditFilters, listAudit } from "@/lib/api"
import type { AuditChange, AuditFilters, AuditPage, AuditRow } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import { EMPTY, formatDate, formatInt, formatTime } from "@/lib/format"
import { cn } from "@/lib/utils"
import { SettingsTabs } from "@/pages/settings/tabs"

/**
 * Settings → «Журнал действий»: кто, когда и что поменял в аккаунте
 * (AMEND-35 шаг 2).
 *
 * Эстетика — industrial-utilitarian «приборная панель», та же, что у соседних
 * экранов Settings: бриф велит держать их стиль, и журнал, нарисованный иначе,
 * читался бы как чужой модуль. Шрифты, палитра и панели — общие из
 * `index.css`/`tailwind.config.ts`, своего здесь только одно: колонка «что
 * изменилось» читается как сверка, а не как текст — «было» приглушено, стрелка
 * в цвете акцента, «стало» в полную силу, а удалённое значение зачёркнуто.
 * Глаз находит, *что* поменялось, не дочитывая строку.
 *
 * Экран по-русски, хотя консоль англоязычна: бриф требует подписи действий
 * по-русски, а таблица с русскими действиями под английскими заголовками
 * читалась бы как недоперевод. Даты — в формате консоли (`lib/format`), чтобы
 * время в журнале сверялось с временем в списке транзакций один в один.
 *
 * Аккаунт экран не выбирает и не передаёт: ручка берёт его из сессии. Не админ
 * получает от ручки 403, и экран объясняет это словами, а не пустой таблицей.
 */

/** «Любой» в выпадающих списках. Не пустая строка: её Radix Select не принимает. */
const ANY = "__any__"

type Period = "all" | "1D" | "7D" | "30D" | "custom"

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: "all", label: "Всё время" },
  { value: "1D", label: "Сутки" },
  { value: "7D", label: "7 дней" },
  { value: "30D", label: "30 дней" },
  { value: "custom", label: "Свой" },
]

const PERIOD_DAYS: Record<"1D" | "7D" | "30D", number> = { "1D": 1, "7D": 7, "30D": 30 }

const DAY_MS = 24 * 60 * 60 * 1000

interface Range {
  from?: string
  to?: string
  /** Начало позже конца: запрос не уходит, экран говорит, что не так. */
  invalid?: boolean
}

/**
 * Границы периода как ISO-моменты для ручки.
 *
 * Окна считаются от `anchor` — момента, когда период выбрали, а не от «сейчас»
 * на каждый запрос: иначе начало окна уезжало бы на секунды между страницами,
 * и строка на стыке могла бы показаться дважды или пропасть.
 *
 * Свой период — по дням в часовом поясе браузера, обе даты включительно: «по
 * 5-е» значит «до конца 5-го», а не «до его полуночи».
 */
function rangeFor(period: Period, anchor: number, since: string, until: string): Range {
  if (period === "all") return {}
  if (period !== "custom") {
    return { from: new Date(anchor - PERIOD_DAYS[period] * DAY_MS).toISOString() }
  }
  if (since && until && since > until) return { invalid: true }
  return {
    from: since ? new Date(`${since}T00:00:00`).toISOString() : undefined,
    to: until ? new Date(`${until}T23:59:59.999`).toISOString() : undefined,
  }
}

const RU_PLURAL = new Intl.PluralRules("ru-RU")

function records(count: number) {
  const form = RU_PLURAL.select(count)
  const word = form === "one" ? "запись" : form === "few" ? "записи" : "записей"
  return `${formatInt(count)} ${word}`
}

export function AuditPage() {
  const { identity } = useAuth()
  const [period, setPeriod] = React.useState<Period>("all")
  const [anchor, setAnchor] = React.useState(() => Date.now())
  const [since, setSince] = React.useState("")
  const [until, setUntil] = React.useState("")
  const [person, setPerson] = React.useState(ANY)
  const [action, setAction] = React.useState(ANY)
  const [offset, setOffset] = React.useState(0)
  const [reload, setReload] = React.useState(0)

  const [data, setData] = React.useState<AuditPage | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<Error | null>(null)
  const [filters, setFilters] = React.useState<AuditFilters | null>(null)

  const range = rangeFor(period, anchor, since, until)

  React.useEffect(() => {
    const controller = new AbortController()
    getAuditFilters(controller.signal)
      .then(setFilters)
      // Без значений фильтров журнал всё равно читается: списки останутся с
      // одним «все», а отказ самой ручки (403, сеть) покажет запрос журнала.
      .catch(() => undefined)
    return () => controller.abort()
  }, [])

  React.useEffect(() => {
    if (range.invalid) {
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    listAudit(
      {
        from: range.from,
        to: range.to,
        user_id: person === ANY ? undefined : Number(person),
        action: action === ANY ? undefined : action,
        offset,
      },
      controller.signal,
    )
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setData(null)
        setError(cause instanceof Error ? cause : new Error("Request failed"))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [range.from, range.to, range.invalid, person, action, offset, reload])

  const filtered = period !== "all" || person !== ANY || action !== ANY

  function resetFilters() {
    setPeriod("all")
    setSince("")
    setUntil("")
    setPerson(ANY)
    setAction(ANY)
    setOffset(0)
  }

  const forbidden = error instanceof ApiError && error.status === 403
  const items = range.invalid ? [] : (data?.items ?? [])
  const total = range.invalid ? 0 : (data?.total ?? 0)
  const first = total === 0 ? 0 : offset + 1
  const last = Math.min(offset + items.length, total)

  return (
    <>
      <PageHeader title="Settings" tabs={<SettingsTabs />} />
      <div className="p-5">
        <div className="mx-auto max-w-6xl space-y-4">
          {forbidden ? (
            <NotAnAdmin message={error?.message ?? ""} />
          ) : (
            <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-3">
                <div>
                  <span className="stencil-label text-fg-muted">Журнал действий</span>
                  <h2 className="mt-0.5 text-sm font-semibold text-fg">
                    {identity?.account_name ?? "Аккаунт"}
                  </h2>
                </div>
                <span className="max-w-md text-xs text-fg-subtle">
                  Кто и что менял в этом аккаунте. Просмотр сюда не пишется — только изменения и
                  входы.
                </span>
              </header>

              <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
                <Segmented
                  label="Период"
                  options={PERIOD_OPTIONS}
                  value={period}
                  onChange={(value) => {
                    setPeriod(value)
                    setAnchor(Date.now())
                    setOffset(0)
                  }}
                />

                <Select
                  value={person}
                  onValueChange={(next) => {
                    setPerson(next)
                    setOffset(0)
                  }}
                >
                  <SelectTrigger className="w-56" aria-label="Кто">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>Все люди</SelectItem>
                    {/* Только те, кто в журнале есть: пункт, дающий ноль строк,
                        читался бы как сломанный фильтр. */}
                    {(filters?.people ?? []).map((item) => (
                      <SelectItem key={item.user_id} value={String(item.user_id)}>
                        {item.email ?? `#${item.user_id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={action}
                  onValueChange={(next) => {
                    setAction(next)
                    setOffset(0)
                  }}
                >
                  <SelectTrigger className="w-60" aria-label="Действие">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>Все действия</SelectItem>
                    {(filters?.actions ?? []).map((item) => (
                      <SelectItem key={item.action} value={item.action}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {filtered && (
                  <Button size="sm" variant="ghost" onClick={resetFilters}>
                    <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
                    Сбросить
                  </Button>
                )}

                <span className="ml-auto text-xs text-fg-muted">
                  {loading && !data ? EMPTY : records(total)}
                </span>
              </div>

              {period === "custom" && (
                <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-sunken px-4 py-2">
                  <label className="stencil-label text-fg-muted" htmlFor="audit-from">
                    С
                  </label>
                  <Input
                    id="audit-from"
                    type="date"
                    value={since}
                    onChange={(event) => {
                      setSince(event.target.value)
                      setOffset(0)
                    }}
                    className="w-36"
                  />
                  <label className="stencil-label text-fg-muted" htmlFor="audit-to">
                    По
                  </label>
                  <Input
                    id="audit-to"
                    type="date"
                    value={until}
                    onChange={(event) => {
                      setUntil(event.target.value)
                      setOffset(0)
                    }}
                    className="w-36"
                  />
                  {range.invalid ? (
                    <Notice tone="warn">Начало периода позже конца — поменяйте даты местами.</Notice>
                  ) : (
                    <span className="text-xs text-fg-muted">
                      {!since && !until
                        ? "Выберите хотя бы одну дату — до тех пор журнал не ограничен по времени."
                        : "Обе даты включительно, по часам этого браузера."}
                    </span>
                  )}
                </div>
              )}

              {error && !forbidden ? (
                <div className="p-6 text-center">
                  <p className="text-sm font-medium text-decline">{error.message}</p>
                  <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
                    Таблица намеренно пуста: пустой журнал читался бы как «никто ничего не
                    делал», а это совсем другое.
                  </p>
                  <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
                    Повторить
                  </Button>
                </div>
              ) : loading && !data ? (
                <ListSkeleton />
              ) : items.length === 0 ? (
                // Пустое состояние — вне таблицы: внутри ячейки на всю ширину 1040 px
                // текст центровался за правым краем телефона, и видно было «Ра».
                <div
                  className={cn("px-6 py-10 text-center transition-opacity", loading && "opacity-60")}
                  aria-busy={loading}
                >
                  <p className="text-sm font-medium text-fg">
                    {range.invalid
                      ? "Период задан задом наперёд."
                      : filtered
                        ? "С этими фильтрами записей нет."
                        : "Журнал пока пуст."}
                  </p>
                  <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
                    {filtered
                      ? "Расширьте период или снимите фильтр по человеку и действию."
                      : "Сюда попадают входы в консоль и все изменения: правила, списки, люди, имя аккаунта, загрузки файлов."}
                  </p>
                  {filtered && (
                    <Button className="mt-4" size="sm" onClick={resetFilters}>
                      Сбросить фильтры
                    </Button>
                  )}
                </div>
              ) : (
                <div
                  className={cn("overflow-x-auto transition-opacity", loading && "opacity-60")}
                  aria-busy={loading}
                >
                  {/* Четыре колонки прибиты (704 px), «Что изменилось» живёт на остатке.
                      При прежних 760 px ей на узком экране доставалось ~56 px, и значение
                      рвалось по букве в строку высотой в экран, — поэтому нижняя граница
                      стоит и у самой колонки (`min-w` у списка изменений), и у таблицы. */}
                  <Table className="min-w-[1040px]">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-28">Когда</TableHead>
                        <TableHead className="w-48">Кто</TableHead>
                        <TableHead className="w-44">Действие</TableHead>
                        <TableHead className="w-56">Объект</TableHead>
                        <TableHead>Что изменилось</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((row) => (
                        <AuditTableRow key={row.id} row={row} />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {!error && (
                <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
                  <span className="text-xs text-fg-muted">
                    {total === 0
                      ? "0 из 0"
                      : `${formatInt(first)}–${formatInt(last)} из ${formatInt(total)}`}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      disabled={offset === 0 || loading}
                      onClick={() => setOffset((value) => Math.max(0, value - AUDIT_PAGE_SIZE))}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
                      Назад
                    </Button>
                    <Button
                      size="sm"
                      disabled={range.invalid || !data?.has_more || loading}
                      onClick={() => setOffset((value) => value + AUDIT_PAGE_SIZE)}
                    >
                      Дальше
                      <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
                    </Button>
                  </div>
                </footer>
              )}
            </section>
          )}
        </div>
      </div>
    </>
  )
}

// ── строки ───────────────────────────────────────────────────────────────────

function AuditTableRow({ row }: { row: AuditRow }) {
  return (
    <TableRow className="align-top">
      <TableCell className="whitespace-nowrap">
        <div className="text-fg">{formatDate(row.created_at)}</div>
        <div className="text-xs text-fg-muted">{formatTime(row.created_at)}</div>
      </TableCell>
      <TableCell>
        <div className="break-all text-fg">{row.actor_email ?? EMPTY}</div>
        {row.ip && <div className="text-xs text-fg-subtle">{row.ip}</div>}
      </TableCell>
      {/* Неудачный вход цветом не выделен: цвет в консоли значит исход
          скоринга (approve/review/decline), и «жёлтый вход» читался бы как
          «вход на ручной проверке». Его отличает сама подпись. */}
      <TableCell className="font-medium text-fg">{row.action_label}</TableCell>
      <TableCell>
        <ObjectCell row={row} />
      </TableCell>
      <TableCell>
        <ChangesCell row={row} />
      </TableCell>
    </TableRow>
  )
}

function ObjectCell({ row }: { row: AuditRow }) {
  const label = row.object_label ?? (row.object_id ? `#${row.object_id}` : null)
  if (!row.object_type_label && !label) return <span className="text-fg-subtle">{EMPTY}</span>
  return (
    <div>
      {row.object_type_label && (
        <div className="stencil-label text-fg-muted">{row.object_type_label}</div>
      )}
      {label && <div className="mt-0.5 break-all text-fg">{label}</div>}
    </div>
  )
}

/**
 * «Что изменилось» в трёх видах — по `shape`, который решила ручка.
 *
 * У созданного показывается только «стало», у удалённого — только «было»,
 * зачёркнутым; пустые стороны пропускаются: «Комментарий: —» у нового
 * значения списка ничего не сообщает. Правка — «было → стало» целиком, пустая
 * сторона там как раз информация («срока не было — стал»).
 */
function ChangesCell({ row }: { row: AuditRow }) {
  const changes = row.changes.filter((change) => {
    if (row.shape === "created") return !isBlank(change.after)
    if (row.shape === "removed") return !isBlank(change.before)
    return true
  })
  if (changes.length === 0) return <span className="text-fg-subtle">{EMPTY}</span>

  return (
    <dl className="grid min-w-[18rem] grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5">
      {changes.map((change) => (
        <React.Fragment key={change.field}>
          <dt className="whitespace-nowrap text-fg-muted">{change.label}</dt>
          <dd className="break-words">
            <ChangeValue shape={row.shape} change={change} />
          </dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

function ChangeValue({ shape, change }: { shape: AuditRow["shape"]; change: AuditChange }) {
  if (shape === "created") {
    return <span className="text-fg">{formatValue(change.field, change.after)}</span>
  }
  if (shape === "removed") {
    return (
      <span className="text-fg-muted line-through decoration-fg-subtle">
        {formatValue(change.field, change.before)}
      </span>
    )
  }
  return (
    <>
      <span className="text-fg-muted">{formatValue(change.field, change.before)}</span>
      <span aria-hidden className="mx-1.5 font-semibold text-accent">
        →
      </span>
      <span className="sr-only"> стало </span>
      <span className="font-medium text-fg">{formatValue(change.field, change.after)}</span>
    </>
  )
}

/** Чем не подошёл неудачный вход — значения из `backend/auth/service.py`. */
const FACTORS: Record<string, string> = { password: "пароль", code: "код" }

/** Момент из jsonb: ручки пишут `datetime.isoformat()` — с буквой `T`. */
const ISO_MOMENT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

function isBlank(value: unknown) {
  return value === null || value === undefined || value === ""
}

function formatValue(field: string, value: unknown): string {
  if (isBlank(value)) return EMPTY
  if (typeof value === "boolean") return value ? "вкл." : "выкл."
  if (field === "factor") return FACTORS[String(value)] ?? String(value)
  if (typeof value === "number") {
    return field === "size_bytes" ? `${formatInt(value)} Б` : formatInt(value)
  }
  if (typeof value === "string") {
    return ISO_MOMENT.test(value) ? `${formatDate(value)} ${formatTime(value)}` : value
  }
  return JSON.stringify(value)
}

// ── состояния ────────────────────────────────────────────────────────────────

/** Вошедший — не админ. Объясняем, а не показываем пустоту. */
function NotAnAdmin({ message }: { message: string }) {
  return (
    <section className="animate-panel-in rounded border border-line bg-surface p-6 shadow-panel">
      <div className="flex items-center gap-2 text-fg-muted">
        <Lock className="h-4 w-4" strokeWidth={1.75} />
        <span className="stencil-label">Только для админа</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-fg">{message}</p>
      <p className="mt-2 text-xs leading-relaxed text-fg-muted">
        Ваш доступ работает как обычно — закрыт только журнал. Он показывает действия всех людей
        аккаунта, поэтому его видит админ.
      </p>
    </section>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  )
}
